import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { GlossaryWizard } from    "@/components/wizards/glossary/ObjectWizard";
import { GlossaryItem, updateObject, createObject } from "@/lib/stores/objectsStore";
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';
import { useTabContext } from '@/contexts/TabContext';
import { useTabTitleSync } from '@/hooks/useTabTitleSync';
import { useContentHighlighting } from '@/hooks/useContentHighlighting';
import { getPendingChangeById, removePendingChange, subscribeToPendingChanges } from '@/lib/stores/pendingChangesStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useSplitViewAwareParams } from '@/hooks/useSplitViewAwareParams';
import { useSplitViewContext } from '@/components/layout/SplitViewContent';
import { useAutoSave } from '@/hooks/useAutoSave';

export default function GlossaryItemPage() {
  const { itemId } = useSplitViewAwareParams<{ itemId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { updateTabTitle, updateTabUnsavedChanges, updateTabCleanup, requestTabClose, tabs } = useTabContext();
  const splitViewContext = useSplitViewContext();
  const isSplitView = splitViewContext?.isSplitView ?? false;
  const { glossaryItems, isLoading: contextLoading, loadData } = useGlossaryDataContext();
  const { selectedGame } = useSimplifiedUserData();
  
  const [glossaryItem, setGlossaryItem] = useState<GlossaryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pendingChangesVersion, setPendingChangesVersion] = useState(0);
  const hasTriedLoading = React.useRef(false);
  
  // Track unsaved changes and save handler for auto-save
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const saveHandlerRef = useRef<(() => Promise<void>) | undefined>();
  
  // Track lock release function for explicit tab close
  const lockReleaseRef = useRef<(() => Promise<void>) | undefined>();
  
  // Track highlights in the glossary item content
  const { trackFromData } = useContentHighlighting();

  // Check if this is a new object creation
  const isNewObject = itemId === 'new';
  
  // Sync tab title with item name (handles both initial load and name changes)
  // Skip in split view mode - split view tabs are managed separately
  useTabTitleSync(
    !isNewObject ? glossaryItem?.name : undefined, 
    { disabled: isSplitView }
  );
  
  // Get folder from URL params for new objects
  const folderFromUrl = searchParams.get('folder');

  // Subscribe to pending changes to re-render when they change
  useEffect(() => {
    const unsubscribe = subscribeToPendingChanges(() => {
      setPendingChangesVersion(v => v + 1);
    });
    return unsubscribe;
  }, []);

  // Reset state immediately when itemId changes to prevent showing stale data
  useEffect(() => {
    if (!isNewObject) {
      setGlossaryItem(null);
      setIsLoading(true);
      setNotFound(false);
      hasTriedLoading.current = false;
    }
  }, [itemId, isNewObject]);

  // Handle new object setup
  useEffect(() => {
    if (isNewObject && selectedGame?.id && !isSplitView) {
      // Set up tab title for new object
      const tabId = location.pathname;
      updateTabTitle(tabId, 'New Object');
      setIsLoading(false);
    }
  }, [isNewObject, selectedGame?.id, location.pathname, updateTabTitle, isSplitView]);

  // Load glossary item and merge with any pending changes
  useEffect(() => {
    if (isNewObject || !itemId) {
      if (!isNewObject && !itemId) {
        setNotFound(true);
        setIsLoading(false);
      }
      return;
    }

    // Try to find the item first - it might already be in the store/cache
    const item = glossaryItems.find(gi => gi.id === itemId);
    
    if (item) {
      // Check for pending changes and merge them
      const pendingChange = getPendingChangeById(itemId);
      if (pendingChange?.pendingData) {
        console.log('[ObjectPage] Found pending changes for item:', itemId);
        // Merge pending changes with the original item (pending data takes priority)
        const mergedItem: GlossaryItem = {
          ...item,
          ...pendingChange.pendingData,
          // Preserve fields that shouldn't be overwritten
          id: item.id,
          game_id: item.game_id,
          created_at: item.created_at,
          created_by: item.created_by,
        };
        setGlossaryItem(mergedItem);
      } else {
        // Only update if item changed to avoid unnecessary re-renders
        setGlossaryItem(prev => prev?.id === item.id ? prev : item);
      }
      setNotFound(false);
      setIsLoading(false);
    } else if (contextLoading) {
      // Item not found but still loading, wait
      setIsLoading(true);
    } else if (!hasTriedLoading.current) {
      // Item not found and loading completed, trigger a load once
      // This only happens when navigating directly to a URL without cached data
      hasTriedLoading.current = true;
      loadData();
    } else {
      // Already tried loading and item still not found
      console.warn(`Library object ${itemId} not found in loaded items`);
      setNotFound(true);
      setIsLoading(false);
    }
  }, [itemId, glossaryItems, contextLoading, pendingChangesVersion, isNewObject, loadData]);

  // Redirect to library if item not found (only for existing objects)
  useEffect(() => {
    if (notFound && !isLoading && !isNewObject) {
      console.warn(`Library item ${itemId} not found or access denied. Redirecting to library.`);
      navigate('/library', { replace: true });
    }
  }, [notFound, isLoading, itemId, navigate, isNewObject]);

  // Tab title sync is now handled by useTabTitleSync hook above

  // Track highlights in the glossary item content
  // Create a signature of the glossary item data to trigger re-tracking when content changes
  const glossaryItemSignature = glossaryItem ? JSON.stringify(glossaryItem.data) : '';
  
  useEffect(() => {
    if (glossaryItem && !isNewObject) {
      // Debounce the tracking update
      const timeoutId = setTimeout(() => {
        trackFromData(glossaryItem);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [glossaryItemSignature, glossaryItem, trackFromData, isNewObject]);

  // Handle saving a new object (create)
  const handleSave = useCallback(async (itemData: any) => {
    if (!selectedGame?.id) return;

    try {
      const newObject = await createObject(
        {
          ...itemData,
          game_id: selectedGame.id,
          type: 'Library Object',
          folder: folderFromUrl || itemData.folder || null,
        },
        toast
      );

      // Refresh the objects to get the latest data
      await loadData();

      toast({
        title: 'Success',
        description: 'Object created successfully',
      });

      // Clear tab unsaved changes BEFORE navigation to prevent stale hasUnsavedChanges
      // from persisting through the wizard remount (key change causes old wizard to unmount,
      // losing its cleanup, while the tab retains the old unsaved changes flag)
      const activeTab = tabs.find(t => t.isActive);
      if (activeTab) {
        updateTabUnsavedChanges(activeTab.id, false);
      }

      // Navigate to the newly created object page
      navigate(`/library/${newObject.id}`, { replace: true });
    } catch (error) {
      console.error('Error creating object:', error);
      toast({
        title: 'Error',
        description: 'Failed to create object',
        variant: 'destructive',
      });
    }
  }, [selectedGame?.id, folderFromUrl, toast, loadData, navigate, tabs, updateTabUnsavedChanges]);

  // Handle updating an existing object
  const handleUpdate = useCallback(async (updatedItem: {
    id?: string;
    game_id?: string;
    name: string;
    tags: string[];
    identifiers: string[];
    data: any[];
    blueprint_id?: string | null;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version?: number | { major: number; minor: number; patch: number };
  }) => {
    if (!itemId || !glossaryItem) return;

    try {
      // Destructure to exclude version from spread since we'll use the existing one
      const { version, ...itemDataWithoutVersion } = updatedItem;
      
      await updateObject(
        {
          ...itemDataWithoutVersion,
          id: itemId,
          game_id: glossaryItem.game_id,
          type: 'Library Object',
          version: glossaryItem.version, // Use existing version from glossaryItem
        },
        'patch', // versionBump parameter
        undefined, // changeSummary parameter
        toast
      );

      // Clear any pending changes for this item since we just saved
      removePendingChange(itemId);
      console.log('[ObjectPage] Cleared pending changes for item:', itemId);

      // NOTE: Don't call loadData() here - updateObject uses optimistic updates
      // so the store is already updated. Calling loadData() can cause component
      // remount which would release the edit lock.

      // Update local glossaryItem state to reflect the saved changes
      // This ensures the wizard has the latest data without a remount
      setGlossaryItem(prev => prev ? {
        ...prev,
        name: updatedItem.name,
        tags: updatedItem.tags,
        identifiers: updatedItem.identifiers,
        data: updatedItem.data,
        blueprint_id: updatedItem.blueprint_id,
        blueprint_version: updatedItem.blueprint_version,
      } : prev);

      // Tab title update is handled by useTabTitleSync hook

      toast({
        title: 'Success',
        description: 'Object updated successfully',
      });
    } catch (error) {
      console.error('Error updating glossary item:', error);
      toast({
        title: 'Error',
        description: 'Failed to update object',
        variant: 'destructive',
      });
    }
  }, [itemId, glossaryItem, toast, location.pathname, updateTabTitle]);

  // Handler to refresh data when lock becomes available (another user released their lock)
  const handleRefreshData = useCallback(async () => {
    await loadData();
    // The useEffect that watches glossaryItems will update the local glossaryItem state
  }, [loadData]);

  // Register lock release cleanup handler when lock release function is ready
  useEffect(() => {
    if (!lockReleaseRef.current) return;
    
    // Find the ACTUAL tab ID by looking for the active tab
    // (Don't use location.pathname - that's the content path, not the tab ID!)
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) return;
    
    const tabId = activeTab.id;
    
    
    // Register the lock release function to be called before tab closes
    updateTabCleanup(tabId, lockReleaseRef.current);
    
    return () => {
      // Clean up the handler when component unmounts
      updateTabCleanup(tabId, undefined);
    };
  }, [tabs, location.pathname, lockReleaseRef.current, updateTabCleanup]);

  const handleClose = useCallback(async () => {
    // Find the ACTUAL tab ID (not location.pathname!)
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) return;
    
    const tabId = activeTab.id;
    
    
    // Request to close the tab (will show unsaved changes dialog if needed)
    // The onBeforeClose handler (lock release) will be called automatically by TabContext
    const canClose = await requestTabClose(tabId);
    
    if (canClose) {
      // If the tab was closed, navigate to library page
      navigate('/library');
    }
  }, [tabs, location.pathname, requestTabClose, navigate]);

  // Handle unsaved changes state from wizard
  const handleUnsavedChangesChange = useCallback((hasChanges: boolean, saveHandler: () => Promise<void>, discardHandler?: () => void) => {
    // Find the ACTUAL tab ID
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) return;
    
    const tabId = activeTab.id;
    // Track for auto-save
    setHasUnsavedChanges(hasChanges);
    saveHandlerRef.current = saveHandler;
    // Update tab context
    updateTabUnsavedChanges(tabId, hasChanges, saveHandler, discardHandler);
  }, [tabs, updateTabUnsavedChanges]);

  // Enable auto-save based on user settings
  useAutoSave({
    hasUnsavedChanges,
    saveHandler: saveHandlerRef.current,
    enabled: !isNewObject, // Only auto-save for existing objects, not new ones
  });

  // Show loading state (only for existing objects)
  if (!isNewObject && (isLoading || contextLoading)) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading object..." />
      </div>
    );
  }

  // For new objects, check if we have required data
  if (isNewObject) {
    if (!selectedGame?.id) {
      return (
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="lg" text="Loading..." />
        </div>
      );
    }

    // Render wizard for new object (no initialData means create mode)
    return (
      <div className="h-full w-full bg-surface">
        <GlossaryWizard
          key="object-new"
          inline={true}
          onClose={handleClose}
          onSave={handleSave}
          onUnsavedChangesChange={handleUnsavedChangesChange}
          onLockReleaseReady={(releaseFunc) => { lockReleaseRef.current = releaseFunc; }}
        />
      </div>
    );
  }

  // If notFound or no glossaryItem, show loading while redirecting (redirect happens in useEffect)
  if (notFound || !glossaryItem) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Redirecting..." />
      </div>
    );
  }

  // CRITICAL: Ensure the glossaryItem ID matches the URL itemId before rendering
  // This prevents showing stale data when the glossaryItem state hasn't updated yet
  if (glossaryItem.id !== itemId) {
    console.log('[ObjectPage] ID mismatch detected - glossaryItem.id:', glossaryItem.id, 'vs itemId:', itemId);
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading object..." />
      </div>
    );
  }

  // Create a stable key that includes both itemId and blueprint to force remount when switching objects
  const wizardKey = `${itemId}-${glossaryItem?.blueprint_id || 'no-blueprint'}`;

  return (
    <div className="h-full w-full bg-surface">
      <GlossaryWizard
        key={wizardKey}
        inline={true}
        onClose={handleClose}
        initialData={glossaryItem}
        onUpdate={handleUpdate}
        onUnsavedChangesChange={handleUnsavedChangesChange}
        onRefreshData={handleRefreshData}
        onLockReleaseReady={(releaseFunc) => { lockReleaseRef.current = releaseFunc; }}
      />
    </div>
  );
}
